import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Calendar, Clock, Trophy, Users } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { LoadingState, ErrorState, EmptyState } from '@/components/ui/loading'
import { api, queryKeys, type Character, type Report, type Fight } from '@/lib/api'
import { cn, formatDuration, getDifficultyColor } from '@/lib/utils'

interface ReportSelectionProps {
  character: Character
  onFightSelect: (report: Report, fight: Fight) => void
  onBack: () => void
  className?: string
}

const DIFFICULTIES = {
  1: 'LFR',
  3: 'Normal',
  4: 'Heroic',
  5: 'Mythic',
}

export const ReportSelection: React.FC<ReportSelectionProps> = ({
  character,
  onFightSelect,
  onBack,
  className,
}) => {
  const [selectedReport, setSelectedReport] = React.useState<Report | null>(null)

  const {
    data: reports,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.reports(character.name, character.realm, character.region),
    queryFn: () => api.getReports(character.name, character.realm, character.region),
  })

  const handleReportSelect = (report: Report) => {
    setSelectedReport(selectedReport?.code === report.code ? null : report)
  }

  const handleFightSelect = (fight: Fight) => {
    if (selectedReport) {
      onFightSelect(selectedReport, fight)
    }
  }

  if (isLoading) {
    return <LoadingState>Loading reports...</LoadingState>
  }

  if (error) {
    return (
      <ErrorState
        title="Failed to Load Reports"
        message={error.message}
        onRetry={() => refetch()}
      />
    )
  }

  if (!reports || reports.length === 0) {
    return (
      <EmptyState
        title="No Reports Found"
        description={`No recent reports found for ${character.name}. Make sure the character has recent raid logs on WarcraftLogs.`}
        action={<Button onClick={onBack}>Search Different Character</Button>}
      />
    )
  }

  return (
    <div className={cn('space-y-6', className)}>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Select Report & Fight</h2>
          <p className="text-muted-foreground">
            Choose a raid report and specific encounter to analyze for {character.name}
          </p>
        </div>
        <Button variant="outline" onClick={onBack}>
          Back to Search
        </Button>
      </div>

      <div className="space-y-4">
        {reports.map((report) => (
          <Card 
            key={report.code}
            className={cn(
              'cursor-pointer transition-colors hover:bg-accent/50',
              selectedReport?.code === report.code && 'ring-2 ring-primary'
            )}
            onClick={() => handleReportSelect(report)}
          >
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-lg">{report.title}</CardTitle>
                  <CardDescription className="flex items-center space-x-4 mt-2">
                    <span className="flex items-center space-x-1">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(report.startTime).toLocaleDateString()}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Clock className="h-4 w-4" />
                      <span>{formatDuration(report.endTime - report.startTime)}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Users className="h-4 w-4" />
                      <span>{report.owner}</span>
                    </span>
                  </CardDescription>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium">{report.zone.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {report.fights.length} encounters
                  </p>
                </div>
              </div>
            </CardHeader>

            {selectedReport?.code === report.code && (
              <CardContent className="pt-0">
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3">Select Encounter:</h4>
                  <div className="grid gap-2">
                    {report.fights.map((fight) => (
                      <div
                        key={fight.id}
                        className={cn(
                          'flex items-center justify-between p-3 rounded-lg border transition-colors hover:bg-accent cursor-pointer'
                        )}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleFightSelect(fight)
                        }}
                      >
                        <div className="flex items-center space-x-3">
                          <div className={cn(
                            'h-2 w-2 rounded-full',
                            fight.kill ? 'bg-green-500' : 'bg-red-500'
                          )} />
                          <div>
                            <p className="font-medium">{fight.name}</p>
                            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                              <span 
                                className="font-medium"
                                style={{ color: getDifficultyColor(DIFFICULTIES[fight.difficulty as keyof typeof DIFFICULTIES]) }}
                              >
                                {DIFFICULTIES[fight.difficulty as keyof typeof DIFFICULTIES]}
                              </span>
                              <span>•</span>
                              <span>{formatDuration(fight.endTime - fight.startTime)}</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-3 text-sm">
                          <div className="text-right">
                            {fight.kill ? (
                              <div className="flex items-center space-x-1 text-green-600">
                                <Trophy className="h-4 w-4" />
                                <span>Kill</span>
                              </div>
                            ) : (
                              <span className="text-red-600">
                                {fight.percentage.toFixed(1)}%
                              </span>
                            )}
                          </div>
                          <Button size="sm" variant="outline">
                            Analyze
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>
    </div>
  )
}